declare global {
	interface External {
		notify: Function;
	}

	interface Window {
		_GLOBALS: {};
		webpublicationNamespace: {};
		readonly external: External;
	}
}

import Utils from './';
import UserAgentUtils from './userAgent';
import { Api } from '../typings/api';
const Q = require('q');
const I18n = require('react-redux-i18n').I18n;

if (UserAgentUtils.isMobileApp) {
	window._GLOBALS = {
		main: {
			javaScriptInterface: {
				callCallBackList: (pId, data) => NativeApi.handleDefer(pId, data)
			}
		}
	};

	window.webpublicationNamespace = {
		notify: (type: string) => {
			if (type === 'applicationForeground' && UserAgentUtils.isIOS()) {
				NativeApi.killPendingDownloads();
			}
		}
	};
}

export enum StatusBarColor {
	White = 'white',
	Black = 'black',
	Transparent = 'transparent'
}

const methodWithCallbackList = [
	'getDirTreeZip',
	'getFileTreeZip',
	'getFreeSpace',
	'getUsedSpace',
	'buyEntity',
	'getBoughtEntities',
	'getPricesForEntities',
	'downloadFile',
	'unzipFile'
];

interface PromiseWrapper {
	deferred: Q.Deferred<{}>;
	method: string;
}

export enum DownloadPriority {
	Low = 'low',
	Normal = 'normal',
	High = 'high'
}

interface DownloadFileParams {
	fileId: number;
	galleryConfig: Api.Config;
	fileUrl: string;
	destFolder: string;
	destFileName: string;
	priority: DownloadPriority;
	parameters: string;
}

interface DownloadFileRequest extends DownloadFileParams {
	deferred: Q.Deferred<void>;
}

interface DownloadFileReturn {
	promise: Q.Promise<void>;
	requestId: number;
}

class NativeApi {

	public static targetUrl: string = 'http://jsinterface.native';
	private static pId: number = 0;
	private static fileId: number = 0;
	private static fileIdpIdMap: { [k: number]: number } = {};
	private static defers: { [k: number]: PromiseWrapper } = {};
	private static downloadFileQueue: DownloadFileRequest[] = [];
	private static downloadFilePromise?: Q.Promise<void>;

	public static removeDownloadFromQueue = (fileId): void => {
		const downloadRequest = NativeApi.downloadFileQueue.find(item => item.fileId === fileId);
		if (downloadRequest) {
			downloadRequest.deferred.reject('cancel');
			NativeApi.downloadFileQueue.splice(NativeApi.downloadFileQueue.indexOf(downloadRequest), 1);
		}
	}

	public static getNativePath = (): string => {
		return '../../';
	}

	public static handleDefer = (pId: number, data: {}): void => {
		const defer = NativeApi.defers[pId];
		if (defer) {
			if (defer.method === 'downloadFile') {
				if (typeof (data) === 'object') {
					defer.deferred.notify(data);
				} else if (data === 0 || data === 499) {
					defer.deferred.reject();
				} else {
					defer.deferred.resolve();
				}
			} else {
				defer.deferred.resolve(data);
			}
		}
	}

	public static stopProcessById = (pId: number): Q.Promise<{}> => NativeApi.executeApiCall('stopProcessById', [pId]);

	/* 	External methods */

	/* 	Directories */

	public static copyDirectory = (directory: string, directoryDest: string): Q.Promise<{}> => NativeApi.executeApiCall('copyDirectory', [directory, directoryDest]);

	public static createDirectory = (directory: string): Q.Promise<{}> => NativeApi.executeApiCall('createDirectory', [directory]);

	public static existDirectory = (directory: string): Q.Promise<boolean> => NativeApi.executeApiCall('existDirectory', [directory]) as Q.Promise<boolean>;

	public static moveDirectory = (directory: string, directoryDest: string): Q.Promise<{}> => NativeApi.executeApiCall('moveDirectory', [directory, directoryDest]);

	public static removeDirectory = (directory: string): Q.Promise<{}> => NativeApi.executeApiCall('removeDirectory', [directory]);


	/* 	Files */

	public static copyFile = (file: string, fileDest: string): Q.Promise<{}> => NativeApi.executeApiCall('copyFile', [file, fileDest]);

	public static createFile = (file: string, content: string): Q.Promise<{}> => NativeApi.executeApiCall('createFile', [file, content]);

	public static downloadFile = (
		galleryConfig: Api.Config,
		fileUrl: string,
		destFolder: string,
		destFileName: string,
		priority: DownloadPriority,
		parameters: string = ''
	): DownloadFileReturn => {
		const fileId = NativeApi.getFileId();
		const deferred = NativeApi.addToDownloadQueue({ fileId, galleryConfig, fileUrl, destFolder, destFileName, priority, parameters });

		if (!NativeApi.downloadFilePromise) {
			NativeApi.executeNextDownload();
		}

		return {
			promise: deferred.promise,
			requestId: fileId
		};
	}

	public static stopDownload = (requestId: number): void => {
		const pId = NativeApi.fileIdpIdMap[requestId];

		if (pId) {
			NativeApi.stopProcessById(pId);
			NativeApi.defers[pId].deferred.reject('cancel');
		} else {
			NativeApi.removeDownloadFromQueue(requestId);
		}
	}

	public static existFile = (file: string): Q.Promise<boolean> => NativeApi.executeApiCall('existFile', [file]) as Q.Promise<boolean>;

	public static moveFile = (file: string, fileDest: string): Q.Promise<{}> => NativeApi.executeApiCall('moveFile', [file, fileDest]);

	public static removeFile = (file: string): Q.Promise<{}> => NativeApi.executeApiCall('removeFile', [file]);

	public static unzipFile = (file: string): Q.Promise<{}> => NativeApi.executeApiCall('unzipFile', [file]);


	/* 	General */

	public static getStatusBarColor = (): Q.Promise<{}> => NativeApi.executeApiCall('getStatusBarColor');

	public static hasStatusBar = (): Q.Promise<boolean> => NativeApi.executeApiCall('hasStatusBar') as Q.Promise<boolean>;

	public static openActivity = (url: string): Q.Promise<{}> => NativeApi.executeApiCall('openActivity', [url]);

	public static openFileNatively = (url: string): Q.Promise<{}> => NativeApi.executeApiCall('openFileNatively', [url]);

	public static openVideoActivity = (url: string): Q.Promise<{}> => NativeApi.executeApiCall('openVideoActivity', [url]);

	public static setStatusBarColor = (color: StatusBarColor): Q.Promise<{}> => NativeApi.executeApiCall('setStatusBarColor', [color]);


	/* 	Information */

	public static getFreeSpace = (): Q.Promise<number> => NativeApi.executeApiCall('getFreeSpace') as Q.Promise<number>;

	public static getDeviceType = (): Q.Promise<{}> => NativeApi.executeApiCall('getDeviceType');

	public static getUsedSpace = (): Q.Promise<number> => NativeApi.executeApiCall('getUsedSpace') as Q.Promise<number>;

	public static getVersion = (): Q.Promise<string> => NativeApi.executeApiCall('getVersion') as Q.Promise<string>;


	/* 	Social */
	// on Android API 28, the title doesn't seem to work and will display [object Object] instead of te string
	public static shareApp = (title: string, url: string): Q.Promise<{}> => NativeApi.executeApiCall('shareApp', [title, url, I18n.t('share')]);


	/* 	In-app purchase */
	// TODO: uncomment when galleryConfig.inAppPurchaseGooglePlayPrefix / galleryConfig.inAppPurchaseiTunesPrefix are defined
	/*public static buyEntity = (galleryConfig: AppConfig, entityStoreId: string): Q.Promise<{}> => {
		const inAppPurchasePrefix = Utils.isAndroid() ? galleryConfig.inAppPurchaseGooglePlayPrefix : galleryConfig.inAppPurchaseiTunesPrefix;
		const resolvedEntityStoreId = inAppPurchasePrefix + entityStoreId;
		return NativeApi.executeApiCall('buyEntity', [resolvedEntityStoreId]);
	}

	public static getBoughtEntities = (): Q.Promise<{}> => NativeApi.executeApiCall('getBoughtEntities');

	public static getPricesForEntities = (galleryConfig: AppConfig, entityStoreIds: string[]): Q.Promise<{}> => {
		if (entityStoreIds) {
			const inAppPurchasePrefix = Utils.isAndroid() ? galleryConfig.inAppPurchaseGooglePlayPrefix : galleryConfig.inAppPurchaseiTunesPrefix;
			let entityStoreIdsStr = '';
			entityStoreIds.forEach(entityStoreId => {
				if (entityStoreIdsStr) {
					entityStoreIdsStr += ';';
				}
				entityStoreIdsStr += inAppPurchasePrefix + entityStoreId;
			});

			return NativeApi.executeApiCall('getPricesForEntities', [entityStoreIdsStr]);
		}
		return Promise.reject({});
	}*/


	/* 	Legacy */

	public static createDirJs = (dirName: string): Q.Promise<{}> => NativeApi.executeApiCall('createDirJs', [dirName]);

	public static writeFileTxtJs = (contentFile: string, pathFileName: string): Q.Promise<{}> => NativeApi.executeApiCall('writeFileTxtJs', [contentFile, pathFileName]);

	public static moveFileJs = (pathFileName: string, newPathFile: string): Q.Promise<{}> => NativeApi.executeApiCall('moveFileJs', [pathFileName, newPathFile]);

	public static openFrame = (
		urlSlider: string,
		relatif?: string,
		x?: number,
		y?: number,
		width?: string,
		height?: string): Q.Promise<{}> => {

		if (!UserAgentUtils.isMobileApp) {
			window.open(urlSlider);
		}

		return NativeApi.executeApiCall('openFrame', [relatif, urlSlider, x, y, width, height]);
	}

	public static deleteFileJs = (pathFileName: string): Q.Promise<{}> => NativeApi.executeApiCall('deleteFileJs', [pathFileName]);

	public static deleteDirJs = (pathFileName: string): Q.Promise<{}> => NativeApi.executeApiCall('deleteDirJs', [pathFileName]);

	public static getDirTreeZip = (urlAndCmd: string, dataJson: string): Q.Promise<{}> => NativeApi.executeApiCall('get_x_or_y_tree_zip', [urlAndCmd, dataJson]);

	public static getFileTreeZip = (urlAndCmd: string, dataJson: string): Q.Promise<{}> => NativeApi.executeApiCall('get_x_or_y_tree_zip', [urlAndCmd, dataJson]);

	public static killPendingDownloads = (): void => {
		NativeApi.downloadFileQueue.forEach(downloadRequest => downloadRequest.deferred.reject('killed'));
		NativeApi.downloadFileQueue = [];

		Object.keys(NativeApi.defers).forEach(key => {
			const pid = parseInt(key, 10);
			const { method, deferred } = NativeApi.defers[pid];
			if (method === 'downloadFile') {
				deferred.reject('killed');
			}
		});
	}

	private static getPid = (): number => ++NativeApi.pId;

	private static getFileId = (): number => ++NativeApi.fileId;

	private static executeApiCall = (method: string, args: Array<string | number | undefined> = [], pId: number = NativeApi.getPid()): Q.Promise<{}> => {
		const deferred: Q.Deferred<{}> = Q.defer();

		NativeApi.defers[pId] = { deferred, method };
		NativeApi.sendRequest(method, pId, args, deferred);

		return deferred.promise
			.finally(() => delete NativeApi.defers[pId]);
	}

	private static sendRequest = (method: string, pId: number, args: Array<string | number | undefined>, deferred: Q.Deferred<{}>): void => {

		if (!UserAgentUtils.isMobileApp) {
			deferred.reject();
			return;
		}

		args.push(pId);
		const requestData = { method, 'args[]': [...args] };
		const requestUrl = Utils.dataToUrl(requestData, true);

		if (UserAgentUtils.isWindows()) {
			// Windows has a special handler
			window.external.notify(requestUrl);
		} else {
			const headers = [{ key: 'Content-Type', value: '*; charset=UTF-8' }];
			Utils.promiseXHR('GET', `${NativeApi.targetUrl}?${requestUrl}`, headers, undefined, undefined, undefined, undefined, true)
				.then(result => {
					const isMethodInCallbackList = !methodWithCallbackList.includes(method);
					if (isMethodInCallbackList) {
						const { responseText } = result;
						let response;

						switch (responseText) {
							case 'true':
								response = true;
								break;
							case 'false':
								response = false;
								break;
							default:
								response = responseText;
						}
						NativeApi.handleDefer(pId, response);
					}
				})
				.catch(result => deferred.reject(result));
		}
	}

	private static addToDownloadQueue = (downloadParams: DownloadFileParams): Q.Deferred<void> => {
		const deferred: Q.Deferred<void> = Q.defer();
		const downloadRequest = { ...downloadParams, deferred};
		const downloadFileQueueToSort = [...NativeApi.downloadFileQueue, downloadRequest];
		const lowPriorities: DownloadFileRequest[] = [];
		const normalPriorities: DownloadFileRequest[] = [];
		const highPriorities: DownloadFileRequest[] = [];

		downloadFileQueueToSort.forEach(downloadRequest => {
			if (downloadRequest.priority === DownloadPriority.High) {
				highPriorities.push(downloadRequest);
			} else if (downloadRequest.priority === DownloadPriority.Normal) {
				normalPriorities.push(downloadRequest);
			} else {
				lowPriorities.push(downloadRequest);
			}
		});

		NativeApi.downloadFileQueue = [ ...highPriorities, ...normalPriorities, ...lowPriorities];
		return deferred;
	}

	private static executeNextDownload = (): void => {
		if (NativeApi.downloadFileQueue.length > 0) {
			const downloadRequest = NativeApi.downloadFileQueue.splice(0, 1)[0];
			const { galleryConfig, fileUrl, destFolder, destFileName, parameters, deferred, fileId } = downloadRequest;

			if (galleryConfig.service) {
				// nativeReq param is needed to handle a case when calls are made to several domains (different application urls from ASP_SERVERS and from galleryConfig)
				const modifiedFileUrl = fileUrl + Utils.getUrlSeparator(fileUrl) + 'nativeReq=true';
				const token = galleryConfig.service.token;
				NativeApi.downloadFilePromise = NativeApi.existDirectory(destFolder)
					.then(directoryExist => directoryExist ? {} : NativeApi.createDirectory(destFolder))
					.then(() => UserAgentUtils.isIOS() ? Utils.isResourceAvailable(modifiedFileUrl) : {})
					.then(() => {
						const pId = NativeApi.getPid();
						NativeApi.fileIdpIdMap[fileId] = pId;
						return NativeApi.executeApiCall('downloadFile', [modifiedFileUrl, `${destFolder}/${destFileName}`, parameters, token], pId);
					})
					.progress(progress => deferred.notify(progress))
					.then(() => UserAgentUtils.isOnlinePromise())
					.then((isOnline) => isOnline ? deferred.resolve() : deferred.reject())
					.catch((result) => deferred.reject(result))
					.finally(() => {
						delete NativeApi.fileIdpIdMap[fileId];
						NativeApi.downloadFilePromise = undefined;
						NativeApi.executeNextDownload();
					});
			} else {
				NativeApi.executeNextDownload();
			}
		}
	}

	constructor() {
		UserAgentUtils.subscribeToConnectionIsDown(NativeApi.killPendingDownloads);
	}
}

export const nativeApi = new NativeApi;

export default NativeApi;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/nativeApi.ts